import { type ReactNode, useEffect, useRef } from 'react';

import { ShortcutRegistry } from './shortcut-registry';
import { ShortcutManagerContext } from './ShortcutManagerContext';

// ─── Provider ─────────────────────────────────────────────────────────────────

interface ShortcutManagerProviderProps {
  children: ReactNode;
  /**
   * DOM element to listen on. Defaults to `document`.
   * Override to limit shortcut handling to a specific subtree (rare).
   */
  target?: Document | HTMLElement;
  /**
   * Whether to attach the listener in the capture phase.
   * Default: true — ERP shortcuts fire before React's synthetic event handlers,
   * giving them the highest chance to preventDefault / stopPropagation.
   */
  capture?: boolean;
}

/**
 * Provides a ShortcutRegistry to the React subtree.
 *
 * Mount ONCE near the app root (inside ERPCoreProvider).
 * Attaches a single `keydown` event listener; all shortcut dispatch goes
 * through the central ShortcutRegistry — no per-hook DOM listeners.
 *
 * @example
 * <ShortcutManagerProvider>
 *   <App />
 * </ShortcutManagerProvider>
 */
export function ShortcutManagerProvider({
  children,
  target,
  capture = true,
}: ShortcutManagerProviderProps) {
  // Create registry once (stable across re-renders)
  const registryRef = useRef<ShortcutRegistry | null>(null);
  registryRef.current ??= new ShortcutRegistry();
  const registry = registryRef.current;

  // Attach the single global keydown listener
  useEffect(() => {
    const el: Document | HTMLElement = target ?? document;

    function handleKeyDown(event: Event): void {
      registry.dispatch(event as KeyboardEvent);
    }

    el.addEventListener('keydown', handleKeyDown, { capture });

    return () => {
      el.removeEventListener('keydown', handleKeyDown, { capture });
    };
  }, [registry, target, capture]);

  return (
    <ShortcutManagerContext.Provider value={{ registry }}>
      {children}
    </ShortcutManagerContext.Provider>
  );
}
